import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type FocusEventHandler,
  type ReactNode,
} from 'react';

import { useFormControlState } from '../../../core/hooks/useFormControlState.js';
import { useId } from '../../../core/hooks/useId.js';
// oxlint-disable-next-line typescript/no-deprecated
import type { FormControlState } from '../../../core/types/form-control-state.js';
import { parseBoolean } from '../../../core/utils/parse-boolean.js';
import {
  FormFieldMessagesAction,
  type FormFieldMessagesReducerAction,
} from '../state/formfield-messages-state-reducer.js';
import { FormFieldAction } from '../state/formfield-state-reducer.js';
import type { FormFieldMessageContextState } from '../types/formfield-messages.types.js';
import { useFormFieldContext } from './useFormFieldContext.js';
import { useFormFieldMessagesContext } from './useFormFieldMessagesContext.js';

/**
 * When validation errors first become visible.
 * - `'immediate'` (default): an error is surfaced as soon as it exists (after
 *   the field is touched). It stays visible and updates live while the user is
 *   editing.
 * - `'after-blur'`: an error becomes visible on blur. While the user is editing
 *   (focus inside), a shown error stays visible until all errors are resolved,
 *   then clears; a new error raised while editing is held back until the next
 *   blur. Used by complex inputs (e.g. `FilterField`) where intermediate values
 *   are expected to be invalid.
 *
 * See `forms/validation/api-proposals/validation-visibility.md`.
 * @internal
 */
export type ValidationVisibility = 'immediate' | 'after-blur';

/**
 * Props for `useFormControl`. Mirrors the inputs of the legacy form-control
 * hook stack that Strato controls used to call individually.
 * @internal — Strato controls only.
 */
export interface UseFormControlProps {
  /** Control id passed directly to the form control. */
  propId?: string;
  /** Disabled state passed directly to the form control. */
  propDisabled?: boolean;
  /** Aria-disabled state passed directly to the form control. */
  propAriaDisabled?: boolean | 'true' | 'false';
  /** Required state passed directly to the form control. */
  propRequired?: boolean;
  /** Read-only state passed directly to the form control. */
  propReadOnly?: boolean;
  /**
   * Legacy `controlState` plumbing for hint/error.
   * Removed by APPDEV-8298.
   */
  // oxlint-disable-next-line typescript/no-deprecated
  controlState?: FormControlState;
  /**
   * When validation errors first become visible. Defaults to `'immediate'`.
   * @internal
   */
  validationVisibility?: ValidationVisibility;
  /**
   * Control-local validation state used by complex controls while their
   * message-context update has not rendered yet. This does not replace the
   * message context; it only lets after-blur visibility react in the same
   * render as the control's own validation state.
   * Ignored when `validationVisibility === 'immediate'`.
   * @internal
   */
  hasValidationError?: boolean;
}

/** @internal */
export interface UseFormControlReturnType {
  id: string;
  disabled: boolean | undefined;
  required: boolean | undefined;
  readOnly: boolean | undefined;
  ariaDisabled: boolean | undefined;
  inputProps: { 'aria-describedby'?: string; 'aria-invalid': boolean };
  hintProps: { id: string; 'aria-live': 'polite' };
  /** Hint content from the legacy `controlState` prop, if any. */
  hint: ReactNode;
  /** True if the control should render error styling right now. Disabled-gated. */
  showError: boolean;
  /** Always present (defaults to empty state when no FormField wraps). */
  messageState: FormFieldMessageContextState;
  /** Undefined when no FormField wraps the control. */
  messageDispatch: Dispatch<FormFieldMessagesReducerAction> | undefined;
  /**
   * Focus handlers for the element that defines "the field". Only
   * meaningful in `'after-blur'` mode; an empty object otherwise, so callers
   * can spread it unconditionally.
   * @internal
   */
  focusHandlers: {
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
  };
  /**
   * Mark the field touched and reveal any error immediately, ignoring focus.
   * Wire to a control's imperative `validate()` so parent-driven validation
   * paints the error even while focus is still inside the field. No-op in
   * `'immediate'` mode.
   * @internal
   */
  markTouched: () => void;
}

/**
 * Single entry point for Strato controls to wire id, disabled/required/readOnly
 * synchronisation with the FormField context, error/hint plumbing, and ARIA
 * attributes. Has no knowledge of `_Form`; that wiring lives in
 * `useInternalFormRegistration`.
 * @internal — Strato controls only.
 */
export function useFormControl({
  propId,
  propDisabled,
  propAriaDisabled,
  propRequired,
  propReadOnly,
  controlState,
  validationVisibility = 'immediate',
  hasValidationError = false,
}: UseFormControlProps): UseFormControlReturnType {
  const uniqueId = useId();
  const {
    // oxlint-disable-next-line typescript/no-deprecated
    controlId,
    formFieldId,
    required: contextRequired,
    disabled: contextDisabled,
    ariaDisabled: contextAriaDisabled,
    readOnly: contextReadOnly,
    dispatch: formFieldDispatch,
    registerFormControlId,
  } = useFormFieldContext();

  const { state: messageState, dispatch: rawMessageDispatch } =
    useFormFieldMessagesContext();

  // `controlState` is a self-contained, consumer-controlled visual override.
  // Isolate it from the message-dispatch/validation system entirely: don't
  // hand out a dispatch a control could use to pollute the shared
  // `FormFieldMessages` state, and don't let that shared state leak into this
  // control's own error/aria display.
  const messageDispatch = controlState ? undefined : rawMessageDispatch;

  const disabled = propDisabled ?? contextDisabled;
  const ariaDisabled = parseBoolean(propAriaDisabled) ?? contextAriaDisabled;
  const required = propRequired ?? contextRequired;
  const readOnly = propReadOnly ?? contextReadOnly;
  const legacyFallbackId = formFieldId ?? controlId;
  const id = propId || legacyFallbackId || uniqueId;

  useEffect(() => {
    formFieldDispatch?.({
      type: FormFieldAction.UPDATE,
      payload: {
        disabled: propDisabled,
        required: propRequired,
        ariaDisabled: parseBoolean(propAriaDisabled),
        readOnly: propReadOnly,
      },
    });
  }, [
    formFieldDispatch,
    propDisabled,
    propRequired,
    propAriaDisabled,
    propReadOnly,
  ]);

  useEffect(() => {
    messageDispatch?.({
      type: FormFieldMessagesAction.DISABLEMESSAGE,
      payload: { isDisabled: disabled },
    });
  }, [disabled, messageDispatch]);

  useEffect(() => registerFormControlId(id), [id, registerFormControlId]);

  useEffect(() => {
    messageDispatch?.({
      type: FormFieldMessagesAction.SETFORMCONTROLID,
      payload: { formControlId: id },
    });
  }, [messageDispatch, id]);

  const [showLegacyError, hint] = useFormControlState(controlState);

  const isAfterBlur = validationVisibility === 'after-blur';

  // `after-blur` state machine — ported from FilterField. `revealed` is sticky:
  // once an error is shown on blur it stays visible while the user edits, until
  // every error is resolved; a new error that appears while editing is held
  // back until the next blur (re-reveal). `markTouched` forces a reveal now,
  // ignoring focus.
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const hasAnyError =
    (!controlState && messageState.showError) ||
    showLegacyError ||
    (isAfterBlur && hasValidationError);

  // Adjust `revealed` during render (React's "adjusting state on prop change"
  // pattern) rather than in an effect, so the visible state is correct on the
  // same render — no lag and no race with a `markTouched()` that runs in the
  // same batch as the error being added.
  let nextRevealed = revealed;
  if (isAfterBlur) {
    if (revealed && !hasAnyError) {
      nextRevealed = false;
    } else if (
      !hasFocusWithin &&
      (messageState.isTouched ?? false) &&
      hasAnyError
    ) {
      nextRevealed = true;
    }
    // The `nextRevealed !== revealed` guard is required because this setter is
    // called during render (the "adjusting state on prop change" pattern above),
    // NOT from an event handler or effect. React's Object.is bail-out inside
    // `dispatchSetState` only runs for updates dispatched outside of render;
    // render-phase updates take a different code path that always schedules
    // another render pass and never compares against the current state. Calling
    // `setRevealed(nextRevealed)` unconditionally therefore re-schedules on
    // every render and trips the "Too many re-renders" bail-out. FilterField is
    // the only `after-blur` consumer and reproduces this immediately on mount.
    if (nextRevealed !== revealed) {
      setRevealed(nextRevealed);
    }
  }

  // Legacy `controlState` has always been an explicit consumer-controlled
  // visual state, exclusive of message-driven validation — restore that
  // exclusivity here so `controlState` fully isolates a control from the
  // shared `FormFieldMessages` state instead of being OR'd with it.
  // `messageState.showError` bypasses the after-blur gate on its own (e.g. a
  // `showImmediately` message forces it true before the field is touched) —
  // mirror that bypass here so the input's error styling matches the message.
  const showValidationError = controlState
    ? false
    : isAfterBlur
      ? (hasAnyError && nextRevealed) || messageState.showError
      : messageState.showError;

  const showError = !disabled && (showValidationError || showLegacyError);

  // Also isolate the returned `messageState`: some controls read it directly
  // (e.g. to seed their own local reducer or ARIA wiring) rather than going
  // through `showError`/`inputProps` above. When `controlState` is set, hand
  // out a synthetic state reflecting `controlState`'s own validity instead of
  // the raw, un-isolated shared state.
  const resolvedMessageState = useMemo<FormFieldMessageContextState>(
    () =>
      controlState
        ? {
            errors: [],
            hints: [],
            isTouched: false,
            showError: showLegacyError,
            isValid: !showLegacyError,
          }
        : messageState,
    [controlState, showLegacyError, messageState],
  );

  const hintId = useId();
  const inputProps = {
    'aria-describedby': hint ? hintId : undefined,
    'aria-invalid': showValidationError || showLegacyError,
  };
  const hintProps = { id: hintId, 'aria-live': 'polite' as const };

  const focusHandlers = useMemo<UseFormControlReturnType['focusHandlers']>(
    () =>
      isAfterBlur
        ? {
            onFocus: () => {
              setHasFocusWithin(true);
              // Clears touched so an error raised while editing stays hidden
              // until the next blur. Already-shown errors are unaffected.
              messageDispatch?.({
                type: FormFieldMessagesAction.SETISTOUCHED,
                payload: { isTouched: false },
              });
            },
            onBlur: (event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setHasFocusWithin(false);
                messageDispatch?.({
                  type: FormFieldMessagesAction.SETISTOUCHED,
                  payload: { isTouched: true },
                });
              }
            },
          }
        : {},
    [isAfterBlur, messageDispatch],
  );

  const markTouched = useCallback(() => {
    if (!isAfterBlur) {
      return;
    }
    messageDispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: true },
    });
    setRevealed(true);
  }, [isAfterBlur, messageDispatch]);

  return {
    id,
    disabled,
    required,
    readOnly,
    ariaDisabled,
    inputProps,
    hintProps,
    hint,
    showError,
    messageState: resolvedMessageState,
    messageDispatch,
    focusHandlers,
    markTouched,
  };
}
